import QRCode from 'qrcode';
import jsPDF from 'jspdf';

/**
 * Genera un código QR único basado en los datos del paquete
 * Usa formato universal compatible con todos los layouts de teclado
 * @param {Object} data - Datos del paquete
 * @param {number} data.idGrupo - ID del grupo
 * @param {number} data.idCliente - ID del cliente
 * @param {number} data.idDestino - ID del destino (idItem)
 * @param {number} data.idUbicacion - ID de la ubicación (paquete)
 * @returns {string} - String codificado para el QR
 */
export const generarCodigoQR = (data) => {
  const { idGrupo, idCliente, idDestino, idUbicacion } = data;
  // Formato universal: G-grupo_C-cliente_D-destino_U-ubicacion
  // Usa solo guiones y guiones bajos (compatibles con todos los teclados)
  return `G-${idGrupo}_C-${idCliente}_D-${idDestino}_U-${idUbicacion}`;
};

/**
 * Decodifica un código QR de cuponera
 * Soporta formato nuevo (G-25_C-8) y formato legacy (G:25|C:8)
 * @param {string} qrData - Datos del QR escaneado
 * @returns {Object|null} - Objeto con los IDs o null si es inválido
 */
export const decodificarCodigoQR = (qrData) => {
  try {
    const result = {};
    
    // Detectar formato y parsear
    if (qrData.includes('_')) {
      // Formato nuevo: G-25_C-8_D-11876_U-25
      const parts = qrData.split('_');
      parts.forEach(part => {
        const [key, value] = part.split('-');
        switch(key) {
          case 'G':
            result.idGrupo = parseInt(value);
            break;
          case 'C':
            result.idCliente = parseInt(value);
            break;
          case 'D':
            result.idDestino = parseInt(value);
            break;
          case 'U':
            result.idUbicacion = parseInt(value);
            break;
          default:
            break;
        }
      });
    } else if (qrData.includes('|')) {
      // Formato legacy: G:25|C:8|D:11876|U:25
      const parts = qrData.split('|');
      parts.forEach(part => {
        const [key, value] = part.split(':');
        switch(key) {
          case 'G':
            result.idGrupo = parseInt(value);
            break;
          case 'C':
            result.idCliente = parseInt(value);
            break;
          case 'D':
            result.idDestino = parseInt(value);
            break;
          case 'U':
            result.idUbicacion = parseInt(value);
            break;
          default:
            break;
        }
      });
    }
    
    // Validar que tenga todos los campos necesarios
    if (result.idGrupo && result.idCliente && result.idDestino && result.idUbicacion) {
      return result;
    }
    
    return null;
  } catch (error) {
    console.error('Error decodificando QR:', error);
    return null;
  }
};

/**
 * Genera una imagen QR como Data URL
 * @param {string} qrData - Datos a codificar en el QR
 * @returns {Promise<string>} - Data URL de la imagen QR
 */
export const generarImagenQR = async (qrData) => {
  try {
    const qrDataUrl = await QRCode.toDataURL(qrData, {
      width: 150,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
    return qrDataUrl;
  } catch (error) {
    console.error('Error generando imagen QR:', error);
    throw error;
  }
};

/**
 * Formatea una fecha en formato DD/MM/YYYY
 * @param {string|Date} fecha - Fecha a formatear
 * @returns {string} - Fecha formateada
 */
const formatearFecha = (fecha) => {
  if (!fecha) return '-';

  // Evitar desfase por UTC: si llega "YYYY-MM-DD" o ISO, tomar solo la parte de fecha
  let date = null;
  if (fecha instanceof Date) {
    date = fecha;
  } else if (typeof fecha === 'string') {
    const match = fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      date = new Date(Number(year), Number(month) - 1, Number(day));
    } else {
      date = new Date(fecha);
    }
  } else {
    date = new Date(fecha);
  }

  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Genera un PDF con la cuponera de cuotas
 * @param {Object} params - Parámetros para generar la cuponera
 * @param {Object} params.paqueteInfo - Información del paquete
 * @param {Array} params.cuotas - Array de cuotas
 * @param {Object} params.clienteData - Datos del cliente
 * @param {string} params.logoBase64 - Logo del tenant en base64
 */
export const generarPDFCuponera = async ({
  paqueteInfo,
  cuotas,
  clienteData,
  logoBase64,
  showFechaSalida = true,
}) => {
  try {
    // Crear documento PDF
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Datos para el QR
    const qrData = generarCodigoQR({
      idGrupo: paqueteInfo.ubicacion?.id,
      idCliente: clienteData.id,
      idDestino: paqueteInfo.item?.id,
      idUbicacion: paqueteInfo.ubicacion?.id
    });

    const qrImageUrl = await generarImagenQR(qrData);

    // Información del paquete para todos los cupones
    const grupo = paqueteInfo.item?.descripcion || '';
    const destino = paqueteInfo.ubicacion?.descripcion || '';
    const fechaSalida = formatearFecha(
      paqueteInfo.fechaInicio ||
      paqueteInfo.fechaInicioViaje ||
      paqueteInfo.item?.fechaInicio
    );
    const nombrePasajero = `${clienteData.nombre} ${clienteData.apellido}`;

    let currentY = 10; // Posición Y inicial
    const margin = 10;
    const altoCuponFila = 60; // Altura reducida para 4 cuotas por página
    const espacioEntreCuotas = 3; // Espacio entre filas de cupones

    // Por cada cuota, generar 1 fila con 3 cupones (Control Pasajero, Control Empresa, Cobranzas)
    cuotas.forEach((cuota, cuotaIndex) => {
      // Verificar si hay espacio en la página
      if (currentY + altoCuponFila > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }

      // Calcular montos para 1er y 2do vencimiento
      const montoBaseBruto = cuota.montoBase || 0;
      const montoDescuento = cuota.montoDescuento || 0;
      const montoBase = Math.max(0, montoBaseBruto - montoDescuento);
      const montoInteres = cuota.montoInteres || 0;
      
      // 1er vencimiento: siempre es el monto base
      const montoPrimerVto = montoBase;
      
      // 2do vencimiento: monto base + interés (si la cuota tiene interés asignado)
      const montoSegundoVto = montoBase + montoInteres;

      // Dibujar la fila de 3 cupones
      dibujarFilaCupones(doc, {
        y: currentY,
        pageWidth,
        pageHeight,
        margin,
        logoBase64,
        grupo,
        destino,
        fechaSalida,
        showFechaSalida,
        nombrePasajero,
        cuotaNro: cuota.cuotaNro,
        primerVto: formatearFecha(cuota.fechaPrimerVto),
        segundoVto: formatearFecha(cuota.fechaSegundoVto),
        montoPrimerVto,
        montoSegundoVto,
        qrImageUrl,
        qrData,
        altoCuponFila
      });

      currentY += altoCuponFila + espacioEntreCuotas;
    });

    // Descargar el PDF directamente
    const fileName = `Cuponera_${nombrePasajero.replace(/\s/g, '_')}_${destino.replace(/\s/g, '_')}.pdf`;
    doc.setProperties({ title: fileName, subject: "Cuponera de Pagos", creator: "Sistema" });
    doc.save(fileName);

  } catch (error) {
    console.error('Error generando PDF de cuponera:', error);
    throw error;
  }
};

/**
 * Dibuja una fila completa con 3 cupones (Control Pasajero, Control Empresa, Cobranzas)
 */
const dibujarFilaCupones = (doc, params) => {
  const {
    y,
    pageWidth,
    margin,
    logoBase64,
    grupo,
    destino,
    fechaSalida,
    showFechaSalida,
    nombrePasajero,
    cuotaNro,
    primerVto,
    segundoVto,
    montoPrimerVto,
    montoSegundoVto,
    qrImageUrl,
    qrData,
    altoCuponFila
  } = params;

  const labels = ['CONTROL PASAJERO', 'CONTROL EMPRESA', 'COBRANZAS'];
  const anchoCupon = (pageWidth - 2 * margin) / 3; // Ancho de cada cupón (1/3 del ancho)
  const espacioEntreColumnas = 0; // Sin espacio entre columnas

  // Dibujar los 3 cupones lado a lado
  labels.forEach((label, index) => {
    const xInicio = margin + (index * anchoCupon);
    
    dibujarCuponIndividual(doc, {
      x: xInicio,
      y: y,
      ancho: anchoCupon,
      alto: altoCuponFila,
      logoBase64,
      label,
      grupo,
      destino,
      fechaSalida,
      showFechaSalida,
      nombrePasajero,
      cuotaNro,
      primerVto,
      segundoVto,
      montoPrimerVto,
      montoSegundoVto,
      qrImageUrl,
      qrData
    });
  });

  // Línea punteada de corte al final de la fila
  doc.setLineDash([2, 2]);
  doc.setDrawColor(150, 150, 150);
  doc.line(margin, y + altoCuponFila, pageWidth - margin, y + altoCuponFila);
  doc.setLineDash([]);
};

/**
 * Dibuja un cupón individual en una columna
 */
const dibujarCuponIndividual = (doc, params) => {
  const {
    x,
    y,
    ancho,
    alto,
    logoBase64,
    label,
    grupo,
    destino,
    fechaSalida,
    showFechaSalida,
    nombrePasajero,
    cuotaNro,
    primerVto,
    segundoVto,
    montoPrimerVto,
    montoSegundoVto,
    qrImageUrl,
    qrData
  } = params;

  const padding = 2;
  const altoCabecera = 10;

  // Fondo de cabecera (blanco)
  doc.setFillColor(255, 255, 255);
  doc.rect(x, y, ancho, altoCabecera, 'F');

  // Borde del cupón (después del fill para que se vea completo)
  doc.setDrawColor(91, 91, 95);
  doc.setLineWidth(0.5);
  doc.rect(x, y, ancho, alto);

  // Línea que separa cabecera del cuerpo usando el mismo borde
  doc.line(x, y + altoCabecera, x + ancho, y + altoCabecera);

  // Logo en la cabecera (si existe y hay espacio)
  if (logoBase64) {
    try {
      const logoWidth = 15;
      const logoHeight = 6;
      doc.addImage(logoBase64, 'PNG', x + padding, y + 2, logoWidth, logoHeight);
    } catch (error) {
      console.error('Error agregando logo:', error);
    }
  }

  // Label en la cabecera (centrado)
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(label, x + ancho / 2, y + 6.5, { align: 'center' });

  // Contenido del cupón
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(6.5);
  
  let contentY = y + altoCabecera + 3;
  const contentX = x + padding + 1;
  const maxWidth = ancho - (2 * padding) - 2;

  // Formatear moneda
  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(monto);
  };

  // 1. PASAJERO PRIMERO (nuevo orden)
  doc.setFont('helvetica', 'bold');
  doc.text('Pasajero:', contentX, contentY);
  contentY += 2.5;
  doc.setFont('helvetica', 'normal');
  const pasajeroLines = doc.splitTextToSize(nombrePasajero, maxWidth);
  doc.text(pasajeroLines, contentX, contentY);
  contentY += pasajeroLines.length * 2.5 + 1.5;

  // 2. GRUPO
  doc.setFont('helvetica', 'bold');
  doc.text('Grupo:', contentX, contentY);
  contentY += 2.5;
  doc.setFont('helvetica', 'normal');
  const grupoLines = doc.splitTextToSize(grupo, maxWidth);
  doc.text(grupoLines, contentX, contentY);
  contentY += grupoLines.length * 2.5 + 1.5;

  // 3. DESTINO Y FECHA SALIDA CON TÍTULOS ALINEADOS (nuevo)
  doc.setFont('helvetica', 'bold');
  // Títulos en la misma línea
  doc.text('Destino:', contentX, contentY);
  if (showFechaSalida) {
    doc.text('Fecha Salida:', contentX + 30, contentY);
  }
  contentY += 2.5;
  // Valores en la línea siguiente
  doc.setFont('helvetica', 'normal');
  if (showFechaSalida) {
    const destinoCorto = doc.splitTextToSize(destino, 28)[0]; // Limitar ancho para destino
    doc.text(destinoCorto, contentX, contentY);
    doc.text(fechaSalida, contentX + 30, contentY);
    contentY += 3;
  } else {
    const destinoLines = doc.splitTextToSize(destino, maxWidth);
    doc.text(destinoLines, contentX, contentY);
    contentY += destinoLines.length * 2.5 + 1;
  }

  // 4. NÚMERO DE CUOTA DESTACADO
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(`Cuota ${cuotaNro}`, contentX, contentY);
  contentY += 3.5;

  // 5. VENCIMIENTOS CON MONTOS EN LA MISMA LÍNEA (nuevo)
  doc.setFontSize(6.5);
  
  // 1er Vencimiento - todo en una línea
  doc.setFont('helvetica', 'bold');
  const primerVtoCompleto = `1er Vto: `;
  doc.text(primerVtoCompleto, contentX, contentY);
  doc.setFont('helvetica', 'normal');
  const primerVtoTexto = `${primerVto} - ${formatearMoneda(montoPrimerVto)}`;
  doc.text(primerVtoTexto, contentX + 12, contentY);
  contentY += 3;

  // 2do Vencimiento - todo en una línea
  doc.setFont('helvetica', 'bold');
  const segundoVtoCompleto = `2do Vto: `;
  doc.text(segundoVtoCompleto, contentX, contentY);
  doc.setFont('helvetica', 'normal');
  const segundoVtoTexto = `${segundoVto} - ${formatearMoneda(montoSegundoVto)}`;
  doc.text(segundoVtoTexto, contentX + 12, contentY);

  // QR Code en la parte inferior del cupón
  if (qrImageUrl) {
    const qrSize = 16; // Reducido para cupón más compacto
    const qrX = x + (ancho - qrSize) / 2; // Centrado horizontalmente
    const qrY = y + alto - qrSize - 2; // Parte inferior
    doc.addImage(qrImageUrl, 'PNG', qrX, qrY, qrSize, qrSize);
  }
};
